import {
  Content,
  Group,
  Icon,
  Item,
  ItemIndicator,
  ItemText,
  Label,
  Portal,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  Separator,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

import { FIELD_SLOT_AUTO_CLASSES, fieldVariants, type FieldSize } from '../field/field.tokens';

/**
 * The fixed-enum select (W6): status, gender, plan mode — values that come from
 * a coded enum and fit on one screen. Anything backed by a table uses
 * `ServerSelect` instead, never this with a large `pageSize`.
 */
export const Select = Root;
export const SelectGroup = Group;
export const SelectValue = Value;

export interface SelectTriggerProps extends ComponentPropsWithoutRef<typeof Trigger> {
  /**
   * The kit-wide field scale, so a select can sit in a row of `sm` controls
   * without being the one thing that is 40px tall. Named `fieldSize` rather than
   * `size` because a Radix trigger is a `<button>`, whose `size` attribute means
   * something else entirely.
   */
  fieldSize?: FieldSize;
  /**
   * `field` (the default) is a select that IS the field. `slot` is a select that
   * sits inside someone else's field — a duration's unit chooser — and therefore
   * gives back the border, padding and ring the island already draws.
   *
   * It lives here rather than in the caller so the one field surface stays the
   * only authority on a select's skin: a caller reaching for the Radix trigger
   * directly is how a picker starts looking *almost* like the field beside it.
   */
  variant?: 'field' | 'slot';
}

export const SelectTrigger = forwardRef<ElementRef<typeof Trigger>, SelectTriggerProps>(
  function SelectTrigger({ className, children, fieldSize, variant = 'field', ...props }, ref) {
    return (
      <Trigger
        ref={ref}
        // Shares the one field surface with Input and Textarea, so a select
        // never looks *almost* like the field beside it. `trigger` is the same
        // shape every picker in the kit composes.
        className={cn(
          variant === 'slot'
            ? [FIELD_SLOT_AUTO_CLASSES, 'flex items-center gap-xs text-start']
            : fieldVariants({ fieldSize, shape: 'trigger' }),
          className,
        )}
        {...props}
      >
        {children}
        <Icon asChild>
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 opacity-60" />
        </Icon>
      </Trigger>
    );
  },
);

export type SelectContentProps = ComponentPropsWithoutRef<typeof Content>;

export const SelectContent = forwardRef<ElementRef<typeof Content>, SelectContentProps>(
  function SelectContent({ className, children, position = 'popper', ...props }, ref) {
    return (
      <Portal>
        <Content
          ref={ref}
          position={position}
          className={cn(
            // The ceiling is the room Radix measured between the trigger and the
            // viewport edge, not a fixed `max-h-96`: 384px is taller than a
            // 320px phone in landscape and than any select opened near the
            // bottom of a page, and the overflow was hidden rather than
            // scrolled, so the last options were simply gone. `overflow-hidden`
            // stays — a Radix select scrolls in its VIEWPORT, between the two
            // scroll buttons below, which is what keeps them visible.
            'relative z-popper max-h-select-available max-w-select-available min-w-32 overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            className,
          )}
          {...props}
        >
          <ScrollUpButton className="flex h-6 items-center justify-center">
            <ChevronUp aria-hidden className="h-4 w-4" />
          </ScrollUpButton>
          <Viewport
            className={cn(
              'p-1',
              // Match the trigger's width so the list never jumps narrower than
              // the control that opened it.
              // The trigger's own width, as a token: the preset names the Radix
              // measurement so no component writes `min-w-[var(--radix-…)]`,
              // which the preset's own amendment note calls a hardcode (U6).
              position === 'popper' ? 'w-full min-w-select-trigger' : '',
            )}
          >
            {children}
          </Viewport>
          <ScrollDownButton className="flex h-6 items-center justify-center">
            <ChevronDown aria-hidden className="h-4 w-4" />
          </ScrollDownButton>
        </Content>
      </Portal>
    );
  },
);

export type SelectItemProps = ComponentPropsWithoutRef<typeof Item>;

export const SelectItem = forwardRef<ElementRef<typeof Item>, SelectItemProps>(function SelectItem(
  { className, children, ...props },
  ref,
) {
  return (
    <Item
      ref={ref}
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pe-md ps-2xl text-label outline-none',
        'focus:bg-secondary focus:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      <span className="absolute start-sm inline-flex h-4 w-4 items-center justify-center">
        <ItemIndicator>
          <Check aria-hidden className="h-4 w-4" />
        </ItemIndicator>
      </span>
      <ItemText>{children}</ItemText>
    </Item>
  );
});

export type SelectLabelProps = ComponentPropsWithoutRef<typeof Label>;

export const SelectLabel = forwardRef<ElementRef<typeof Label>, SelectLabelProps>(
  function SelectLabel({ className, ...props }, ref) {
    return (
      <Label
        ref={ref}
        className={cn(
          'py-1.5 pe-md ps-2xl text-caption font-medium text-muted-foreground',
          className,
        )}
        {...props}
      />
    );
  },
);

export type SelectSeparatorProps = ComponentPropsWithoutRef<typeof Separator>;

export const SelectSeparator = forwardRef<ElementRef<typeof Separator>, SelectSeparatorProps>(
  function SelectSeparator({ className, ...props }, ref) {
    return (
      <Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
    );
  },
);
